import { XMLParser } from 'fast-xml-parser';

/**
 * Extract schema from JSON data, preserving structure
 * @param {Object} jsonData - The JSON data to extract schema from
 * @returns {Object} - Schema structure with sample values preserved
 */
export const extractJsonSchema = (jsonData) => {
  if (!jsonData || typeof jsonData !== 'object') {
    return {};
  }

  const extract = (obj) => {
    if (Array.isArray(obj)) {
      // For arrays, take the first element as schema
      if (obj.length > 0) {
        return [extract(obj[0])];
      }
      return [];
    }

    if (obj === null || typeof obj !== 'object') {
      // Return primitive value as sample
      return obj;
    }

    const schema = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        schema[key] = extract(obj[key]);
      }
    }
    return schema;
  };

  return extract(jsonData);
};

/**
 * Convert XML to JSON and extract schema
 * @param {string} xmlData - The XML string to parse
 * @returns {Object} - Schema structure extracted from XML
 */
export const extractXmlSchema = (xmlData) => {
  if (!xmlData || typeof xmlData !== 'string') {
    return {};
  }

  try {
    const options = {
      ignoreAttributes: false,
      attributeNamePrefix: '@_',
      textNodeName: '#text',
      parseAttributeValue: true,
      parseNodeValue: true,
      trimValues: true,
      ignoreNameSpace: false,
      removeNSPrefix: false,
      parseTrueNumberOnly: false,
      arrayMode: false,
      alwaysCreateTextNode: false
    };

    const parser = new XMLParser(options);
    const jsonObj = parser.parse(xmlData);
    
    // Extract schema from parsed JSON
    return extractJsonSchema(jsonObj);
  } catch (error) {
    console.error('Error parsing XML:', error);
    return {};
  }
};

/**
 * Replace sample values with Entity.attribute format placeholders
 * This is a helper that can be used to prepare the schema for mapping
 * @param {Object} schema - The schema object
 * @returns {Object} - Schema with placeholder values
 */
export const replaceValuesWithPlaceholders = (schema) => {
  const replace = (obj, path = '') => {
    if (Array.isArray(obj)) {
      return obj.map((item, index) => replace(item, `${path}[${index}]`));
    }

    if (obj === null || typeof obj !== 'object') {
      // For primitive values, return a placeholder
      const key = path.split('.').pop() || 'value';
      return `Entity.${key}`;
    }

    const result = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newPath = path ? `${path}.${key}` : key;
        result[key] = replace(obj[key], newPath);
      }
    }
    return result;
  };

  return replace(schema);
};

/**
 * Validate JSON structure
 * @param {string} jsonString - JSON string to validate
 * @returns {Object} - { valid: boolean, error: string|null, data: Object|null }
 */
export const validateJson = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);
    return { valid: true, error: null, data };
  } catch (error) {
    return { valid: false, error: error.message, data: null };
  }
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date string (MM/DD/YYYY)
 */
export const formatContractDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } catch (error) {
    return dateString;
  }
};

/**
 * Extract variable ID from template string format "${123}"
 * @param {string} templateString - Template string in format "${123}"
 * @returns {number|null} - Variable ID or null if invalid format
 */
export const extractVariableIdFromTemplate = (templateString) => {
  if (!templateString || typeof templateString !== 'string') {
    return null;
  }
  const match = templateString.match(/^\$\{(\d+)\}$/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Create variable template string from ID
 * @param {number} variableId - Variable ID
 * @returns {string} - Template string in format "${variableId}"
 */
export const createVariableTemplate = (variableId) => {
  if (variableId === null || variableId === undefined) {
    return '';
  }
  return `\${${variableId}}`;
};

/**
 * Find variable ID by Entity.attribute format (placeholderText)
 * @param {string} placeholderText - Entity.attribute format (e.g., "InsuranceHolder.name")
 * @param {Array} variables - Array of variable objects
 * @param {Array} categories - Array of category objects
 * @returns {number|null} - Variable ID or null if not found
 */
export const findVariableIdByPlaceholderText = (placeholderText, variables, categories) => {
  if (!placeholderText || !variables || !categories) {
    return null;
  }

  const categoryMap = {};
  categories.forEach(cat => {
    categoryMap[cat.id] = cat.name;
  });

  // Split Entity.attribute format
  const parts = placeholderText.split('.');
  if (parts.length !== 2) {
    return null;
  }

  const [entityName, attributeName] = parts;

  // Find matching variable
  const variable = variables.find(v => {
    if (!v.variableCategoryId || !categoryMap[v.variableCategoryId]) {
      return false;
    }
    const categoryName = categoryMap[v.variableCategoryId];
    const variablePlaceholder = v.placeHolderText || v.name;
    return categoryName === entityName && variablePlaceholder === attributeName;
  });

  return variable ? variable.id : null;
};

/**
 * Find Entity.attribute format (placeholderText) by variable ID
 * @param {number} variableId - Variable ID
 * @param {Array} variables - Array of variable objects
 * @param {Array} categories - Array of category objects
 * @returns {string|null} - Entity.attribute format or null if not found
 */
export const findPlaceholderTextByVariableId = (variableId, variables, categories) => {
  if (!variableId || !variables || !categories) {
    return null;
  }

  const categoryMap = {};
  categories.forEach(cat => {
    categoryMap[cat.id] = cat.name;
  });

  const variable = variables.find(v => v.id === variableId);
  if (!variable || !variable.variableCategoryId || !categoryMap[variable.variableCategoryId]) {
    return null;
  }

  const categoryName = categoryMap[variable.variableCategoryId];
  const attributeName = variable.placeHolderText || variable.name;
  return `${categoryName}.${attributeName}`;
};

/**
 * Build bidirectional maps for variable conversion
 * @param {Array} variables - Array of variable objects
 * @param {Array} categories - Array of category objects
 * @returns {Object} - { placeholderToId: Map, idToPlaceholder: Map }
 */
export const buildVariableMaps = (variables, categories) => {
  const placeholderToId = new Map();
  const idToPlaceholder = new Map();

  if (!variables || !categories) {
    return { placeholderToId, idToPlaceholder };
  }

  const categoryMap = {};
  categories.forEach(cat => {
    categoryMap[cat.id] = cat.name;
  });

  variables.forEach(v => {
    if (v.variableCategoryId && categoryMap[v.variableCategoryId]) {
      const categoryName = categoryMap[v.variableCategoryId];
      const attributeName = v.placeHolderText || v.name;
      const placeholderText = `${attributeName}`;
      
      placeholderToId.set(placeholderText, v.id);
      idToPlaceholder.set(v.id, placeholderText);
    }
  });

  return { placeholderToId, idToPlaceholder };
};

/**
 * Convert data mapping from display format (Entity.attribute) to storage format ("${variableId}")
 * @param {Object} dataMapping - Data mapping object with Entity.attribute values
 * @param {Map} placeholderToId - Map from Entity.attribute to variable ID
 * @returns {Object} - Data mapping with "${variableId}" values
 */
export const convertDisplayToStorage = (dataMapping, placeholderToId) => {
  if (!dataMapping || typeof dataMapping !== 'object') {
    return {};
  }

  const convert = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => convert(item));
    }

    if (obj === null || typeof obj !== 'object') {
      // Check if it's a string that matches Entity.attribute pattern
      if (typeof obj === 'string' && obj.includes('.') && !obj.startsWith('${')) {
        const variableId = placeholderToId.get(obj);
        if (variableId) {
          return createVariableTemplate(variableId);
        }
      }
      return obj;
    }

    const result = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        result[key] = convert(obj[key]);
      }
    }
    return result;
  };

  return convert(dataMapping);
};

/**
 * Convert data mapping from storage format ("${variableId}") to display format (Entity.attribute)
 * @param {Object} dataMapping - Data mapping object with "${variableId}" values
 * @param {Map} idToPlaceholder - Map from variable ID to Entity.attribute
 * @returns {Object} - Data mapping with Entity.attribute values
 */
export const convertStorageToDisplay = (dataMapping, idToPlaceholder) => {
  if (!dataMapping || typeof dataMapping !== 'object') {
    return {};
  }

  const convert = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => convert(item));
    }

    if (obj === null || typeof obj !== 'object') {
      // Check if it's a template string "${variableId}"
      if (typeof obj === 'string' && obj.startsWith('${') && obj.endsWith('}')) {
        const variableId = extractVariableIdFromTemplate(obj);
        if (variableId && idToPlaceholder.has(variableId)) {
          return idToPlaceholder.get(variableId);
        }
      }
      return obj;
    }

    const result = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        result[key] = convert(obj[key]);
      }
    }
    return result;
  };

  return convert(dataMapping);
};

/**
 * Convert logic mapping from display format to storage format
 * @param {Object} logicMapping - Logic mapping object with Entity.attribute values
 * @param {Map} placeholderToId - Map from Entity.attribute to variable ID
 * @returns {Object} - Logic mapping with "${variableId}" values
 */
export const convertLogicMappingDisplayToStorage = (logicMapping, placeholderToId) => {
  if (!logicMapping || typeof logicMapping !== 'object') {
    return {};
  }

  const result = {};
  for (const key in logicMapping) {
    if (logicMapping.hasOwnProperty(key)) {
      const value = logicMapping[key];
      if (typeof value === 'string' && value.includes('.') && !value.startsWith('${')) {
        const variableId = placeholderToId.get(value);
        if (variableId) {
          result[key] = createVariableTemplate(variableId);
        } else {
          result[key] = value;
        }
      } else {
        result[key] = value;
      }
    }
  }
  return result;
};

/**
 * Convert logic mapping from storage format to display format
 * @param {Object} logicMapping - Logic mapping object with "${variableId}" values
 * @param {Map} idToPlaceholder - Map from variable ID to Entity.attribute
 * @returns {Object} - Logic mapping with Entity.attribute values
 */
export const convertLogicMappingStorageToDisplay = (logicMapping, idToPlaceholder) => {
  if (!logicMapping || typeof logicMapping !== 'object') {
    return {};
  }

  const result = {};
  for (const key in logicMapping) {
    if (logicMapping.hasOwnProperty(key)) {
      const value = logicMapping[key];
      if (typeof value === 'string' && value.startsWith('${') && value.endsWith('}')) {
        const variableId = extractVariableIdFromTemplate(value);
        if (variableId && idToPlaceholder.has(variableId)) {
          result[key] = idToPlaceholder.get(variableId);
        } else {
          result[key] = value;
        }
      } else {
        result[key] = value;
      }
    }
  }
  return result;
};

let logicMappingEntryIdSeq = 0;

/**
 * @returns {string} Stable unique id for a logic mapping list row (display state only).
 */
export const createLogicMappingEntryId = () => {
  logicMappingEntryIdSeq += 1;
  return `${logicMappingEntryIdSeq}`;
};

/**
 * Convert API logic_mapping object to ordered list entries for the UI.
 * @param {Object} logicMapping - Storage map xpath -> "${variableId}" or raw string
 * @param {Map} idToPlaceholder - variable id -> Entity.attribute
 * @returns {Array<{ id: string, xpath: string, variable: string }>}
 */
export const convertLogicMappingStorageToDisplayEntries = (logicMapping, idToPlaceholder) => {
  if (!logicMapping || typeof logicMapping !== 'object') {
    return [];
  }

  const entries = [];
  let index = 0;
  for (const [xpath, value] of Object.entries(logicMapping)) {
    index += 1;
    let variable = value;
    if (typeof value === 'string' && value.startsWith('${') && value.endsWith('}')) {
      const variableId = extractVariableIdFromTemplate(value);
      if (variableId && idToPlaceholder.has(variableId)) {
        variable = idToPlaceholder.get(variableId);
      }
    }
    entries.push({
      id: `logic-entry-${index}-${createLogicMappingEntryId()}`,
      xpath: typeof xpath === 'string' ? xpath : String(xpath),
      variable: typeof variable === 'string' ? variable : String(variable ?? ''),
    });
  }
  return entries;
};

/**
 * Convert display list entries back to API logic_mapping map. Skips rows with empty xpath.
 * Duplicate xpaths: last row wins (console.warn).
 * @param {Array<{ id?: string, xpath: string, variable: string }>} entries
 * @param {Map} placeholderToId - Entity.attribute -> variable id
 * @returns {Object}
 */
export const convertLogicMappingDisplayEntriesToStorage = (entries, placeholderToId) => {
  if (!Array.isArray(entries)) {
    return {};
  }

  const result = {};
  entries.forEach((entry) => {
    if (!entry || typeof entry.xpath !== 'string' || entry.xpath.trim() === '') {
      return;
    }
    const xpath = entry.xpath;
    const value = entry.variable;
    let storageValue = value;
    if (typeof value === 'string' && value.includes('.') && !value.startsWith('${')) {
      const variableId = placeholderToId.get(value);
      if (variableId) {
        storageValue = createVariableTemplate(variableId);
      }
    }
    if (Object.prototype.hasOwnProperty.call(result, xpath)) {
      console.warn('[contractHelpers] Duplicate xpath in logic mapping; last row wins:', xpath);
    }
    result[xpath] = storageValue;
  });
  return result;
};

